/* AZP-7310: the home page hero dropdown flows from the search field, so it is
 * capped to reach the bottom of the window and no further — it starts 8px below
 * the field and keeps a 16px gap at the viewport's bottom edge (24px together),
 * and never collapses shorter than this. */
const HERO_PANEL_VIEWPORT_INSET = 24;
const HERO_PANEL_MIN_HEIGHT = 240;

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.clearButton = this.querySelector("[data-search-clear]");
    this.isOpen = false;
    // AZP-7310: only the home hero locks the page behind its dropdown (see open()).
    this.isHero = !!this.closest(".bs-hero-search");

    this.setupEventListeners();
    this.syncClearButton();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener("input", this.syncClearButton.bind(this));
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("click", this.onResultsClick.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  /* AZP-7077: the clear button only makes sense once there is something to clear. It
   * is toggled on every keystroke rather than on the debounced change, so it does not
   * lag 300ms behind the field. */
  syncClearButton() {
    if (!this.clearButton) {
      return;
    }

    this.clearButton.hidden = !this.getQuery().length;
  }

  clearSearch() {
    // `close(true)` empties the field and throws away the rendered results.
    this.close(true);
    this.syncClearButton();
    this.input.focus();
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  /* AZP-7077: "See all results" does what the search button has always done, but it
   * is rendered inside the results panel, which now sits outside the form (product
   * cards there carry their own add-to-cart form). So it submits the form by hand. */
  onResultsClick(event) {
    if (event.target.closest("[data-search-clear]")) {
      event.preventDefault();
      this.clearSearch();
      return;
    }

    if (!event.target.closest("[data-search-submit]")) {
      return;
    }

    event.preventDefault();
    this.submitSearch();
  }

  submitSearch() {
    const form = this.querySelector("form.search");

    if (!form || !this.getQuery().length) {
      return;
    }

    // A highlighted option would make onFormSubmit cancel the navigation.
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute("aria-selected", false);

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit();
    } else {
      form.submit();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Every navigable row carries role="option" (AZP-7077). Hidden ones are filtered
    // out through https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null,
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // AZP-7077: the design shows at most 5 keywords and 6 products, so the endpoint
    // is asked for 6 of each resource rather than for the theme setting (4, and
    // "all" would spend that budget across every resource type at once).
    const limit = 6;
    const limitScope = "each";
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    fetch(
      // AZP-7008: `body` (product description) is where compatible appliance
      // model numbers live — without it the dropdown returns nothing for a
      // model number while /search finds the product, so shoppers give up
      // before pressing Enter. Verified: "DV456EWHDWR" 0 -> 1 suggestion.
      `${routes.predictive_search_url}?q=${query}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&section_id=predictive-search&resources[options][fields]=title,body,product_type,variants.title,variants.sku,vendor`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
    this.positionPanel();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getHeaderBottom() {
    const header = document.querySelector(".section-header");
    return header ? header.getBoundingClientRect().bottom : 0;
  }

  /* AZP-7077: on desktop the panel is pinned to the viewport and spans its full
   * width, so it hangs off the bottom of the header rather than off the search box
   * — and the header is sticky, so the edge it hangs off moves as the page does.
   * Figma 4302:2743 hangs it off the logo/search row and covers the menu bar under
   * it, so the edge is `.head-mast`, not the whole `.section-header`. */
  positionPanel() {
    const panel = this.predictiveSearchResults;

    if (window.getComputedStyle(panel).position !== "fixed") {
      /* AZP-7310: a panel that flows from the field — the home page hero dropdown
       * — hangs off the box, not off the header. Cap it so its bottom reaches the
       * bottom of the window and no further: from this element's own bottom edge
       * (where the absolute panel starts) down to the viewport, less the inset. */
      const anchorBottom = this.getBoundingClientRect().bottom;
      const available = window.innerHeight - anchorBottom - HERO_PANEL_VIEWPORT_INSET;
      panel.style.maxHeight = `${Math.max(Math.round(available), HERO_PANEL_MIN_HEIGHT)}px`;
      return;
    }

    const headMast = document.querySelector(".section-header .head-mast");
    const top = Math.round(
      headMast ? headMast.getBoundingClientRect().bottom : this.getHeaderBottom(),
    );
    panel.style.top = `${top}px`;
    panel.style.maxHeight = `${window.innerHeight - top}px`;
  }

  /* AZP-7310: while the hero dropdown is open the page behind it must stay put —
   * only the panel scrolls. `overscroll-behavior` keeps a scroll started inside the
   * panel from chaining out; this keeps a wheel or touch drag started anywhere else
   * from moving the page, and without the layout shift a body `overflow: hidden`
   * would bring. Hero only: the header panel is a fixed sheet with its own backdrop. */
  lockPageScroll() {
    if (!this.isHero || this.pageScrollLock) return;

    const panel = this.predictiveSearchResults;
    this.pageScrollLock = (event) => {
      // Let the results scroll their own overflow; block the page everywhere else.
      // The scroll area is the injected `.bs-suggest`, not the clipped card around it.
      const scroller = panel.querySelector(".bs-suggest") || panel;
      if (scroller.contains(event.target) && scroller.scrollHeight > scroller.clientHeight) {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener("wheel", this.pageScrollLock, { passive: false });
    window.addEventListener("touchmove", this.pageScrollLock, { passive: false });
  }

  unlockPageScroll() {
    if (!this.pageScrollLock) return;

    window.removeEventListener("wheel", this.pageScrollLock, { passive: false });
    window.removeEventListener("touchmove", this.pageScrollLock, { passive: false });
    this.pageScrollLock = null;
  }

  open() {
    this.positionPanel();

    if (!this.repositionPanel) {
      this.repositionPanel = this.positionPanel.bind(this);
      window.addEventListener("scroll", this.repositionPanel, { passive: true });
      window.addEventListener("resize", this.repositionPanel);
    }

    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
      this.lockPageScroll();
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.predictiveSearchResults.removeAttribute("style");

    if (this.repositionPanel) {
      window.removeEventListener("scroll", this.repositionPanel);
      window.removeEventListener("resize", this.repositionPanel);
      this.repositionPanel = null;
    }

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
      this.unlockPageScroll();
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
